import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { publicAsset } from '@/lib/utils';
import { ChevronDown, ChevronRight, Clock, Users, ListChecks, X, Filter, TrendingUp, ArrowUp, ArrowDown, ArrowUpDown, Printer } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { fmtDate, fmtDateTime, toDateInputValue } from '@/lib/format-date';

type Employee = { id: string; fullName?: string; email?: string };
type ByEmployeeRow = {
  employee: Employee;
  totalHours: number;
  jobCardCount: number;
  sessionCount: number;
};
type ByJobCardRow = {
  jobCard: { id: string; jobSheetNumber: string; status: string };
  asset?: { id: string; fleetNumber?: string };
  totalHours: number;
  sessionCount: number;
  firstLog: string | null;
  lastLog: string | null;
};
type Session = {
  id: string;
  logDate: string | null;
  startTime: string | null;
  endTime: string | null;
  hours: number | null;
  remarks: string | null;
};

// Render decimal-hours as "5h 25m". Suppresses zero parts when the other is non-zero
// to avoid noise (e.g. "5h" rather than "5h 0m"; "11m" rather than "0h 11m").
function fmtHM(h: number | null | undefined): string {
  if (h == null) return '—';
  const totalMin = Math.round(h * 60);
  const hh = Math.floor(totalMin / 60);
  const mm = totalMin % 60;
  if (hh === 0 && mm === 0) return '0m';
  if (hh === 0) return `${mm}m`;
  if (mm === 0) return `${hh}h`;
  return `${hh}h ${mm}m`;
}
function initials(name?: string) {
  if (!name) return '—';
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');
}

const STATUS_BADGE: Record<string, 'default' | 'secondary' | 'success' | 'destructive'> = {
  active: 'default',
  completed: 'secondary',
  approved: 'success',
  voided: 'destructive',
};

type SortDir = 'asc' | 'desc';
type SortState<K extends string> = { col: K; dir: SortDir } | null;

// Sort hook — first click on a column sets the supplied default direction, second click flips,
// third click clears (returns to the data's natural / server order).
function useSort<K extends string>(initial: SortState<K> = null) {
  const [state, setState] = React.useState<SortState<K>>(initial);
  const onClick = (col: K, defaultDir: SortDir = 'asc') => {
    setState((cur) => {
      if (!cur || cur.col !== col) return { col, dir: defaultDir };
      if (cur.dir === defaultDir) return { col, dir: defaultDir === 'asc' ? 'desc' : 'asc' };
      return null;
    });
  };
  return { state, onClick };
}

function SortableHead<K extends string>({
  col, label, sort, onClick, defaultDir = 'asc', className,
}: {
  col: K;
  label: React.ReactNode;
  sort: SortState<K>;
  onClick: (col: K, defaultDir?: SortDir) => void;
  defaultDir?: SortDir;
  className?: string;
}) {
  const active = sort?.col === col;
  const Icon = active ? (sort?.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <TableHead
      className={`cursor-pointer select-none hover:text-primary transition-colors ${active ? 'text-primary' : ''} ${className ?? ''}`}
      onClick={() => onClick(col, defaultDir)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon className={`h-3 w-3 ${active ? 'opacity-100' : 'opacity-40'}`} />
      </span>
    </TableHead>
  );
}

function compare(a: any, b: any): number {
  // Nulls sort last regardless of direction.
  const aNil = a == null || a === '';
  const bNil = b == null || b === '';
  if (aNil && bNil) return 0;
  if (aNil) return 1;
  if (bNil) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

function applySort<T, K extends string>(rows: T[], sort: SortState<K>, accessor: (r: T, col: K) => any): T[] {
  if (!sort) return rows;
  const sorted = [...rows].sort((a, b) => compare(accessor(a, sort.col), accessor(b, sort.col)));
  if (sort.dir === 'desc') sorted.reverse();
  // Re-place trailing nulls so reverse() doesn't bring them to the top.
  const nullsAtEnd = sorted.filter((r) => {
    const v = accessor(r, sort.col);
    return v == null || v === '';
  });
  if (sort.dir === 'desc' && nullsAtEnd.length > 0) {
    return [
      ...sorted.filter((r) => {
        const v = accessor(r, sort.col);
        return !(v == null || v === '');
      }),
      ...nullsAtEnd,
    ];
  }
  return sorted;
}

function buildQuery(filter: { from: string; to: string; employees: Employee[] }) {
  const q = new URLSearchParams();
  if (filter.from) q.set('from', filter.from);
  if (filter.to)   q.set('to', filter.to);
  if (filter.employees.length) q.set('employees', filter.employees.map((e) => e.id).join(','));
  return q.toString();
}

// Mobile-viewport detector — on phones we collapse the filter card by default
// and surface the active filters as a short summary so the controls don't
// dominate the screen above the KPIs/table.
function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = () => setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);
  return isMobile;
}

export function ServiceDashboardPage() {
  const today = new Date();
  const thirtyAgo = new Date(today.getTime() - 29 * 86400000);
  // NZ calendar day, not UTC: toISOString() reports yesterday through an NZ
  // morning, so the default window's `to` bound excluded today's services.
  const ymd = toDateInputValue;

  const [from, setFrom] = React.useState(ymd(thirtyAgo));
  const [to, setTo] = React.useState(ymd(today));
  const [selectedEmployees, setSelectedEmployees] = React.useState<Employee[]>([]);
  const [employeeFilterOpen, setEmployeeFilterOpen] = React.useState(false);
  const [employeeSearch, setEmployeeSearch] = React.useState('');
  const isMobile = useIsMobileViewport();
  const [filtersOpen, setFiltersOpen] = React.useState(false);

  const filter = { from, to, employees: selectedEmployees };
  const qs = buildQuery(filter);

  const { data: rows = [], isLoading } = useQuery<ByEmployeeRow[]>({
    queryKey: ['/servicing/dashboard/by-employee', qs],
    queryFn: async () => (await api<ByEmployeeRow[]>(`/servicing/dashboard/by-employee?${qs}`)).data || [],
  });

  // Dropdown options = employees who have at least one TimeLog in the chosen date range.
  // Reuses the by-employee aggregate without the `employees` filter so the user can still
  // see/select people they haven't already filtered to.
  const optionsQs = buildQuery({ from, to, employees: [] });
  const { data: pickerRows = [] } = useQuery<ByEmployeeRow[]>({
    queryKey: ['/servicing/dashboard/by-employee', 'picker', optionsQs],
    queryFn: async () => (await api<ByEmployeeRow[]>(`/servicing/dashboard/by-employee?${optionsQs}`)).data || [],
  });
  const employeeOptions: Employee[] = pickerRows
    .map((r) => r.employee)
    .filter((e): e is Employee => !!e?.id)
    .sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? ''));

  const filteredEmployeeOptions = employeeOptions.filter((e) => {
    if (!employeeSearch) return true;
    const q = employeeSearch.toLowerCase();
    return (e.fullName ?? '').toLowerCase().includes(q) || (e.email ?? '').toLowerCase().includes(q);
  });

  const grandTotalHours = rows.reduce((s, r) => s + r.totalHours, 0);
  const grandSessions   = rows.reduce((s, r) => s + r.sessionCount, 0);
  const maxHours        = rows.reduce((m, r) => Math.max(m, r.totalHours), 0) || 1;

  type EmpCol = 'name' | 'hours' | 'jobCards' | 'sessions';
  const empSort = useSort<EmpCol>({ col: 'hours', dir: 'desc' });
  const sortedRows = applySort<ByEmployeeRow, EmpCol>(rows, empSort.state, (r, col) => {
    if (col === 'name')     return r.employee?.fullName ?? r.employee?.email ?? '';
    if (col === 'hours')    return r.totalHours;
    if (col === 'jobCards') return r.jobCardCount;
    if (col === 'sessions') return r.sessionCount;
    return null;
  });

  return (
    <div className="space-y-6 service-dashboard-print">
      {/* ---- Print-only header (logo + company name + title). Mirrors the
              Run Sheet print template so all paperwork carries a consistent
              brand mark and human-readable context. */}
      <div className="hidden print:block">
        <div className="text-center">
          <img src={publicAsset('atlas-logo.png')} alt="Atlas" className="mx-auto h-12 object-contain" />
          <div className="mt-2 text-base font-semibold tracking-wide uppercase">Atlas Fleet Maintenance</div>
          <h1 className="mt-4 text-xl font-semibold tracking-wide uppercase">Service Dashboard</h1>
          <div className="mt-1 text-xs text-zinc-600">
            {from} to {to}
            {selectedEmployees.length > 0 && (
              <> · {selectedEmployees.length} employee{selectedEmployees.length === 1 ? '' : 's'} filtered</>
            )}
          </div>
          <div className="mt-1 text-[10px] text-zinc-500">
            Printed {fmtDateTime(new Date())}
          </div>
        </div>
        <hr className="my-4 border-zinc-300" />
      </div>

      {/* ---- Hero header (screen only) ---- */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-primary to-primary/80 text-primary-foreground p-6 shadow-sm print:hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none"
             style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Service Dashboard</h1>
            <p className="text-sm opacity-90 mt-1">
              Hours logged by employees on service work — drill into job cards and sessions.
            </p>
          </div>
          <div className="flex items-center gap-4 text-right">
            <div>
              <div className="text-xs uppercase tracking-wide opacity-75">Total Time</div>
              <div className="text-3xl font-bold">{fmtHM(grandTotalHours)}</div>
            </div>
            <div className="hidden sm:block w-px bg-white/30 self-stretch" />
            <div className="hidden sm:block">
              <div className="text-xs uppercase tracking-wide opacity-75">Sessions</div>
              <div className="text-3xl font-bold">{grandSessions}</div>
            </div>
            <div className="hidden sm:block w-px bg-white/30 self-stretch" />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => window.print()}
              title="Print, or pick 'Save as PDF' in the print dialog"
            >
              <Printer className="h-3.5 w-3.5 mr-1.5" />
              Print / PDF
            </Button>
          </div>
        </div>
      </div>

      {/* ---- Filter bar ---- */}
      <Card className="border-border/60 shadow-sm print:hidden">
        {/* Mobile: tap the header to expand/collapse. When collapsed,
            a short summary line shows the active filter state so the
            user can see "what's filtered" without expanding. Desktop
            (md+) keeps the filters permanently visible. */}
        <CardHeader
          className={isMobile ? 'cursor-pointer select-none' : ''}
          onClick={isMobile ? () => setFiltersOpen((v) => !v) : undefined}
          role={isMobile ? 'button' : undefined}
          aria-expanded={isMobile ? filtersOpen : undefined}
          tabIndex={isMobile ? 0 : undefined}
          onKeyDown={isMobile ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFiltersOpen((v) => !v); }
          } : undefined}
        >
          {/* Title row — icon + label + (mobile) chevron */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm font-medium">Filters</CardTitle>
            {isMobile && (
              <div className="ml-auto">
                {filtersOpen
                  ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              </div>
            )}
          </div>
          {/* Summary line — mobile only, shown on its own row below the title */}
          {isMobile && (
            <div className="text-xs text-muted-foreground truncate">
              {fmtDate(from)} – {fmtDate(to)}
              {' · '}
              {selectedEmployees.length === 0
                ? 'All employees'
                : `${selectedEmployees.length} employee${selectedEmployees.length === 1 ? '' : 's'}`}
            </div>
          )}
        </CardHeader>
        <CardContent className={`space-y-3 ${isMobile && !filtersOpen ? 'hidden' : ''}`}>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_2fr_auto] gap-3 items-end">
            <div>
              <Label className="text-xs text-muted-foreground">From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="relative">
              <Label className="text-xs text-muted-foreground">Employees</Label>
              <Button
                variant="outline"
                className="w-full justify-between font-normal"
                onClick={() => setEmployeeFilterOpen((v) => !v)}
                type="button"
              >
                {selectedEmployees.length === 0
                  ? <span className="text-muted-foreground">All employees</span>
                  : <span>{selectedEmployees.length} selected</span>}
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
              {employeeFilterOpen && (
                <div className="absolute z-20 mt-1 w-full max-h-72 overflow-auto rounded-md border bg-popover shadow-md p-2">
                  <Input
                    autoFocus
                    placeholder="Search…"
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    className="mb-2"
                  />
                  {filteredEmployeeOptions.slice(0, 100).map((e) => {
                    const checked = selectedEmployees.some((s) => s.id === e.id);
                    return (
                      <label key={e.id} className="flex items-center gap-2 px-2 py-1 text-sm hover:bg-accent cursor-pointer rounded">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setSelectedEmployees((cur) => checked
                              ? cur.filter((c) => c.id !== e.id)
                              : [...cur, e]);
                          }}
                          className="accent-primary"
                        />
                        <span>{e.fullName || e.email || e.id}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <Button variant="ghost" type="button" onClick={() => {
              setFrom(ymd(thirtyAgo)); setTo(ymd(today)); setSelectedEmployees([]);
            }}>
              <X className="h-4 w-4 mr-1" /> Reset
            </Button>
          </div>
          {selectedEmployees.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {selectedEmployees.map((e) => (
                <Badge key={e.id} variant="secondary" className="gap-1 border border-primary/30 bg-primary/10 text-primary hover:bg-primary/15">
                  {e.fullName || e.email || e.id}
                  <button
                    onClick={() => setSelectedEmployees((cur) => cur.filter((c) => c.id !== e.id))}
                    className="ml-1 hover:text-destructive"
                    type="button"
                  ><X className="h-3 w-3" /></button>
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ---- KPI tiles (screen-only — printed header already carries the totals) ----
              On mobile we show 2-per-row with compact cards so the tiles don't
              dominate the screen above the table. */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 print:hidden">
        <KpiTile icon={Clock}      label="Total Time"     value={fmtHM(grandTotalHours)} accent />
        <KpiTile icon={Users}      label="Active Employees" value={rows.length} />
        <KpiTile icon={ListChecks} label="Sessions"        value={grandSessions} />
      </div>

      {/* ---- Drill-down table ---- */}
      <Card className="border-border/60 shadow-sm">
        {/* Mobile: title and count stack vertically so "Hours by Employee"
            doesn't wrap. Desktop: keep them on one row, justified. */}
        <CardHeader className="pb-3 flex-col items-start gap-1 space-y-0 md:flex-row md:items-center md:justify-between md:gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            <CardTitle className="text-base font-semibold whitespace-nowrap">Hours by Employee</CardTitle>
          </div>
          {rows.length > 0 && (
            <span className="text-xs text-muted-foreground">{rows.length} employees in range</span>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">No logs in the selected range.</div>
          ) : (
            <>
              {/* Desktop (lg+): full sortable table */}
              <div className="hidden lg:block">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="w-8"></TableHead>
                      <TableHead className="w-12">#</TableHead>
                      <SortableHead col="name"     label="Employee"           sort={empSort.state} onClick={empSort.onClick} />
                      <TableHead className="w-[28%]">Hours Distribution</TableHead>
                      <SortableHead col="hours"    label="Total Time"         sort={empSort.state} onClick={empSort.onClick} defaultDir="desc" className="text-right" />
                      <SortableHead col="jobCards" label="Number of Service Completed" sort={empSort.state} onClick={empSort.onClick} defaultDir="desc" className="text-right" />
                      <SortableHead col="sessions" label="Sessions"           sort={empSort.state} onClick={empSort.onClick} defaultDir="desc" className="text-right" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedRows.map((r, i) => (
                      <EmployeeRow key={r.employee?.id ?? `unknown-${i}`} row={r} rank={i + 1} maxHours={maxHours} filter={filter} />
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile: card list mirroring the Assets-page pattern. Same data,
                  same expand-to-drill-down behavior, but tappable cards instead
                  of a horizontally-scrolled table. */}
              <div className="lg:hidden -mx-6 -mb-6 divide-y border-t">
                {sortedRows.map((r, i) => (
                  <MobileEmployeeCard
                    key={r.employee?.id ?? `unknown-${i}`}
                    row={r}
                    rank={i + 1}
                    maxHours={maxHours}
                    filter={filter}
                  />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Print stylesheet — landscape A4 to fit the Hours-by-Employee table.
          AppShell hides chrome via `print:hidden`; here we tighten the
          report itself. */}
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 12mm; }
          body { background: white !important; }
          .shadow, .shadow-sm, .shadow-md, .shadow-lg { box-shadow: none !important; }
          /* Force progress-bar + badge backgrounds to print (Chrome strips them
             by default to save ink). */
          [class*="bg-primary"], [class*="bg-emerald"], [class*="bg-red"], [class*="bg-amber"], [class*="bg-zinc"] {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          /* Tables on the dashboard use sticky drill-down rows; print them
             flat so all rows are inline-readable. */
          .service-dashboard-print table { page-break-inside: auto; }
          .service-dashboard-print tr { page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}

function KpiTile({
  icon: Icon, label, value, accent,
}: {
  icon: any;
  label: string;
  value: React.ReactNode;
  accent?: boolean;
}) {
  return (
    <Card className={`relative overflow-hidden border-border/60 shadow-sm ${accent ? 'bg-gradient-to-br from-primary/5 to-transparent' : ''}`}>
      {accent && <div className="absolute top-0 left-0 h-full w-1 bg-primary" />}
      <CardHeader className="pb-1 p-3 md:p-6">
        <CardTitle className="text-[10px] md:text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 md:gap-2">
          <Icon className={`h-3 w-3 md:h-3.5 md:w-3.5 ${accent ? 'text-primary' : ''}`} />
          <span className="truncate">{label}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
        <div className={`text-xl md:text-3xl font-bold ${accent ? 'text-primary' : ''}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

// ---- Mobile card variants ----
// These mirror EmployeeRow / JobCardRow / sessions-table but render as a
// vertical card list instead of a wide table, since the desktop table
// doesn't fit on a phone screen.

function MobileEmployeeCard({
  row, rank, maxHours, filter,
}: {
  row: ByEmployeeRow;
  rank: number;
  maxHours: number;
  filter: { from: string; to: string; employees: Employee[] };
}) {
  const [open, setOpen] = React.useState(false);
  const employeeId = row.employee?.id;
  const qs = buildQuery({ ...filter, employees: [] }) + (employeeId ? `&employee=${employeeId}` : '');
  const widthPct = Math.max(2, (row.totalHours / maxHours) * 100);

  const { data: jobCards = [], isLoading } = useQuery<ByJobCardRow[]>({
    queryKey: ['/servicing/dashboard/by-job-card', employeeId, filter.from, filter.to],
    queryFn: async () => (await api<ByJobCardRow[]>(`/servicing/dashboard/by-job-card?${qs}`)).data || [],
    enabled: open && !!employeeId,
  });

  return (
    <div className={open ? 'bg-primary/5 border-l-2 border-l-primary' : ''}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 text-left hover:bg-accent/40 transition-colors"
      >
        {/* Identity row — rank + avatar + name/email + chevron */}
        <div className="flex items-center gap-3">
          <span className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
            rank <= 3 ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
          }`}>
            {rank}
          </span>
          <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary/10 text-primary text-xs font-semibold">
            {initials(row.employee?.fullName)}
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm leading-tight truncate">{row.employee?.fullName || '—'}</div>
            {row.employee?.email && (
              <div className="text-xs text-muted-foreground leading-tight truncate">{row.employee.email}</div>
            )}
          </div>
          <span className="shrink-0">
            {open
              ? <ChevronDown className="h-4 w-4 text-primary" />
              : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </span>
        </div>

        {/* Progress bar + inline metrics — span full card width below the
            identity row, instead of being constrained next to the avatar. */}
        <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${widthPct}%` }} />
        </div>
        <div className="mt-1.5 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="font-bold text-primary tabular-nums">{fmtHM(row.totalHours)}</span>
          <span className="tabular-nums">{row.jobCardCount} job{row.jobCardCount === 1 ? '' : 's'}</span>
          <span className="tabular-nums">{row.sessionCount} session{row.sessionCount === 1 ? '' : 's'}</span>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-2">
          {isLoading ? (
            <div className="text-sm text-muted-foreground py-2">Loading job cards…</div>
          ) : jobCards.length === 0 ? (
            <div className="text-sm text-muted-foreground py-2">No job cards in range.</div>
          ) : (
            jobCards.map((j, i) => (
              <MobileJobCard
                key={j.jobCard?.id ?? `unknown-${i}`}
                row={j}
                employeeId={employeeId!}
                filter={filter}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function MobileJobCard({
  row, employeeId, filter,
}: {
  row: ByJobCardRow;
  employeeId: string;
  filter: { from: string; to: string; employees: Employee[] };
}) {
  const [open, setOpen] = React.useState(false);
  const jobCardId = row.jobCard?.id;
  const params = new URLSearchParams();
  if (filter.from) params.set('from', filter.from);
  if (filter.to)   params.set('to', filter.to);
  params.set('employee', employeeId);
  if (jobCardId) params.set('jobCard', jobCardId);

  const { data: sessions = [], isLoading } = useQuery<Session[]>({
    queryKey: ['/servicing/dashboard/sessions', employeeId, jobCardId, filter.from, filter.to],
    queryFn: async () => (await api<Session[]>(`/servicing/dashboard/sessions?${params.toString()}`)).data || [],
    enabled: open && !!jobCardId,
  });

  const status = row.jobCard?.status;
  const badgeVariant = (status && STATUS_BADGE[status]) || 'secondary';

  return (
    <div className="rounded-md border bg-card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full p-2.5 flex items-start gap-2 text-left hover:bg-accent/40 transition-colors"
      >
        <div className="flex-1 min-w-0 space-y-0.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs font-semibold">{row.jobCard?.jobSheetNumber || '—'}</span>
            <Badge variant={badgeVariant} className="text-[10px] py-0 px-1.5">{status || '—'}</Badge>
          </div>
          <div className="text-xs text-muted-foreground truncate">
            Asset: {row.asset?.fleetNumber || '—'}
          </div>
          <div className="text-xs text-muted-foreground">
            {fmtDate(row.firstLog)} <span className="text-primary">→</span> {fmtDate(row.lastLog)}
          </div>
          <div className="flex items-center gap-3 text-xs pt-0.5">
            <span className="font-semibold text-primary tabular-nums">{fmtHM(row.totalHours)}</span>
            <span className="tabular-nums text-muted-foreground">{row.sessionCount} session{row.sessionCount === 1 ? '' : 's'}</span>
          </div>
        </div>
        <span className="shrink-0 self-center">
          {open
            ? <ChevronDown className="h-4 w-4 text-primary" />
            : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </span>
      </button>
      {open && (
        <div className="border-t bg-background p-2 space-y-1.5">
          {isLoading ? (
            <div className="text-xs text-muted-foreground py-1">Loading sessions…</div>
          ) : sessions.length === 0 ? (
            <div className="text-xs text-muted-foreground py-1">No sessions.</div>
          ) : (
            sessions.map((s) => (
              <div key={s.id} className="text-xs p-2 rounded bg-muted/40">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{fmtDate(s.logDate)}</span>
                  <span className="font-semibold text-primary tabular-nums">{fmtHM(s.hours)}</span>
                </div>
                <div className="text-muted-foreground mt-0.5">
                  {fmtDateTime(s.startTime)} <span className="text-primary">→</span>{' '}
                  {s.endTime
                    ? fmtDateTime(s.endTime)
                    : <Badge variant="default" className="bg-primary text-primary-foreground text-[10px] py-0 px-1.5 ml-0.5">open</Badge>}
                </div>
                {s.remarks && (
                  <div className="text-muted-foreground mt-1 break-words">{s.remarks}</div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function EmployeeRow({
  row, rank, maxHours, filter,
}: {
  row: ByEmployeeRow;
  rank: number;
  maxHours: number;
  filter: { from: string; to: string; employees: Employee[] };
}) {
  const [open, setOpen] = React.useState(false);
  const employeeId = row.employee?.id;
  const qs = buildQuery({ ...filter, employees: [] }) + (employeeId ? `&employee=${employeeId}` : '');
  const widthPct = Math.max(2, (row.totalHours / maxHours) * 100);

  const { data: jobCards = [], isLoading } = useQuery<ByJobCardRow[]>({
    queryKey: ['/servicing/dashboard/by-job-card', employeeId, filter.from, filter.to],
    queryFn: async () => (await api<ByJobCardRow[]>(`/servicing/dashboard/by-job-card?${qs}`)).data || [],
    enabled: open && !!employeeId,
  });

  type JcCol = 'sheet' | 'asset' | 'status' | 'lastLog' | 'hours' | 'sessions';
  const jcSort = useSort<JcCol>({ col: 'hours', dir: 'desc' });
  const sortedJobCards = applySort<ByJobCardRow, JcCol>(jobCards, jcSort.state, (r, col) => {
    if (col === 'sheet')    return r.jobCard?.jobSheetNumber ?? '';
    if (col === 'asset')    return r.asset?.fleetNumber ?? '';
    if (col === 'status')   return r.jobCard?.status ?? '';
    if (col === 'lastLog')  return r.lastLog ? new Date(r.lastLog).getTime() : null;
    if (col === 'hours')    return r.totalHours;
    if (col === 'sessions') return r.sessionCount;
    return null;
  });

  return (
    <>
      <TableRow
        className={`cursor-pointer transition-colors ${open ? 'bg-primary/5 border-l-2 border-l-primary' : 'hover:bg-accent/40'}`}
        onClick={() => setOpen((v) => !v)}
      >
        <TableCell>
          {open
            ? <ChevronDown className="h-4 w-4 text-primary" />
            : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </TableCell>
        <TableCell>
          {rank <= 3 ? (
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
              {rank}
            </span>
          ) : (
            <span className="text-muted-foreground text-xs">{rank}</span>
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary text-xs font-semibold">
              {initials(row.employee?.fullName)}
            </span>
            <div>
              <div className="font-medium leading-tight">{row.employee?.fullName || '—'}</div>
              {row.employee?.email && (
                <div className="text-xs text-muted-foreground leading-tight">{row.employee.email}</div>
              )}
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${widthPct}%` }} />
          </div>
        </TableCell>
        <TableCell className="text-right">
          <span className="font-bold text-primary tabular-nums">{fmtHM(row.totalHours)}</span>
        </TableCell>
        <TableCell className="text-right tabular-nums">{row.jobCardCount}</TableCell>
        <TableCell className="text-right tabular-nums">{row.sessionCount}</TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={7} className="bg-primary/5 p-3 border-l-2 border-l-primary">
            {isLoading ? (
              <div className="text-sm text-muted-foreground py-2">Loading job cards…</div>
            ) : jobCards.length === 0 ? (
              <div className="text-sm text-muted-foreground py-2">No job cards in range.</div>
            ) : (
              <div className="rounded-md border bg-card overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead className="w-8"></TableHead>
                      <SortableHead col="sheet"    label="Job Sheet #"       sort={jcSort.state} onClick={jcSort.onClick} />
                      <SortableHead col="asset"    label="Asset"             sort={jcSort.state} onClick={jcSort.onClick} />
                      <SortableHead col="status"   label="Status"            sort={jcSort.state} onClick={jcSort.onClick} />
                      <SortableHead col="lastLog"  label="First → Last Log"  sort={jcSort.state} onClick={jcSort.onClick} defaultDir="desc" />
                      <SortableHead col="hours"    label="Time"              sort={jcSort.state} onClick={jcSort.onClick} defaultDir="desc" className="text-right" />
                      <SortableHead col="sessions" label="Sessions"          sort={jcSort.state} onClick={jcSort.onClick} defaultDir="desc" className="text-right" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedJobCards.map((j, i) => (
                      <JobCardRow key={j.jobCard?.id ?? `unknown-${i}`} row={j} employeeId={employeeId!} filter={filter} />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function JobCardRow({
  row, employeeId, filter,
}: {
  row: ByJobCardRow;
  employeeId: string;
  filter: { from: string; to: string; employees: Employee[] };
}) {
  const [open, setOpen] = React.useState(false);
  const jobCardId = row.jobCard?.id;
  const params = new URLSearchParams();
  if (filter.from) params.set('from', filter.from);
  if (filter.to)   params.set('to', filter.to);
  params.set('employee', employeeId);
  if (jobCardId) params.set('jobCard', jobCardId);

  const { data: sessions = [], isLoading } = useQuery<Session[]>({
    queryKey: ['/servicing/dashboard/sessions', employeeId, jobCardId, filter.from, filter.to],
    queryFn: async () => (await api<Session[]>(`/servicing/dashboard/sessions?${params.toString()}`)).data || [],
    enabled: open && !!jobCardId,
  });

  type SessCol = 'date' | 'start' | 'end' | 'hours' | 'remarks';
  const sessSort = useSort<SessCol>({ col: 'start', dir: 'desc' });
  const sortedSessions = applySort<Session, SessCol>(sessions, sessSort.state, (r, col) => {
    if (col === 'date')    return r.logDate ? new Date(r.logDate).getTime() : null;
    if (col === 'start')   return r.startTime ? new Date(r.startTime).getTime() : null;
    if (col === 'end')     return r.endTime ? new Date(r.endTime).getTime() : null;
    if (col === 'hours')   return r.hours;
    if (col === 'remarks') return r.remarks ?? '';
    return null;
  });

  const status = row.jobCard?.status;
  const badgeVariant = (status && STATUS_BADGE[status]) || 'secondary';

  return (
    <>
      <TableRow
        className={`cursor-pointer transition-colors ${open ? 'bg-primary/5' : 'hover:bg-accent/40'}`}
        onClick={() => setOpen((v) => !v)}
      >
        <TableCell>
          {open
            ? <ChevronDown className="h-4 w-4 text-primary" />
            : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </TableCell>
        <TableCell className="font-mono text-xs">{row.jobCard?.jobSheetNumber || '—'}</TableCell>
        <TableCell>{row.asset?.fleetNumber || '—'}</TableCell>
        <TableCell><Badge variant={badgeVariant}>{status || '—'}</Badge></TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {fmtDate(row.firstLog)} <span className="text-primary">→</span> {fmtDate(row.lastLog)}
        </TableCell>
        <TableCell className="text-right">
          <span className="font-semibold text-primary tabular-nums">{fmtHM(row.totalHours)}</span>
        </TableCell>
        <TableCell className="text-right tabular-nums">{row.sessionCount}</TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={7} className="bg-background p-3">
            {isLoading ? (
              <div className="text-sm text-muted-foreground py-2">Loading sessions…</div>
            ) : sessions.length === 0 ? (
              <div className="text-sm text-muted-foreground py-2">No sessions.</div>
            ) : (
              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <SortableHead col="date"    label="Date"    sort={sessSort.state} onClick={sessSort.onClick} defaultDir="desc" />
                      <SortableHead col="start"   label="Start"   sort={sessSort.state} onClick={sessSort.onClick} defaultDir="desc" />
                      <SortableHead col="end"     label="End"     sort={sessSort.state} onClick={sessSort.onClick} defaultDir="desc" />
                      <SortableHead col="hours"   label="Duration" sort={sessSort.state} onClick={sessSort.onClick} defaultDir="desc" className="text-right" />
                      <SortableHead col="remarks" label="Remarks" sort={sessSort.state} onClick={sessSort.onClick} />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedSessions.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="text-xs">{fmtDate(s.logDate)}</TableCell>
                        <TableCell className="text-xs">{fmtDateTime(s.startTime)}</TableCell>
                        <TableCell className="text-xs">
                          {s.endTime
                            ? fmtDateTime(s.endTime)
                            : <Badge variant="default" className="bg-primary text-primary-foreground">open</Badge>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{fmtHM(s.hours)}</TableCell>
                        <TableCell className="max-w-md truncate text-xs text-muted-foreground">{s.remarks || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
